// packages/core/src/learning/school-modules/index.ts
// ─────────────────────────────────────────────────────────────────────────
// Registry for SCHOOL-NATIVE LENS PACKS — the worldview lessons attached to
// each school. Mirrors the core-modules/ barrel pattern exactly: each Lens
// Pack lives in its own file (school-modules/<school-id>.ts) exporting one
// `LearningModule` with surface: 'school', and is imported + listed here.
//
// These modules are surface: 'school'. They are assembled into the live
// LEARNING_MODULES array (see LearningData.ts) so that lesson/flashcard/
// route resolution works app-wide — but they are EXCLUDED from the Core Path
// map by getMapModules() (which keeps only surface 'map'/undefined), and they
// do NOT carry assessments. Per Product Decision 1, their completion feeds
// schoolLensProgress, never corePathProgress.
//
// NAMING CONVENTION (locked):
//   module id      = `school-<schoolId>`   e.g. 'school-boglehead-indexing'
//   homeSchool     = <schoolId>            e.g. 'boglehead-indexing'
//   This must match the `nativeModuleId` set in the school's exhibit.
//
// TO ADD A LENS PACK (same three-line ritual as core-modules/lesson batches):
//   1. author school-modules/<school-id>.ts (parse ChatGPT Lens Pack output)
//   2. add ONE import below
//   3. add ONE entry to SCHOOL_MODULES
//
// AUTHORING STATUS: all 39 schools are now registered as module stubs with
// empty `lessons: []`. A stub renders an empty Lens Pack section until its
// lessons are authored. The 4 below the divider are authored exhibits whose
// lesson content is filled in via the generator pass; the rest are stubs.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

// ── Authored Lens Pack imports ────────────────────────────────────────────
import { SCHOOL_BOGLEHEAD_INDEXING_MODULE } from './boglehead-indexing';
import { SCHOOL_ADULTING_LIFE_SKILLS_MODULE } from './adulting-life-skills';
import { SCHOOL_ANTI_SCAM_MODULE } from './anti-scam';
import { SCHOOL_CREDIT_OPTIMIZATION_MODULE } from './credit-optimization';

// ── Stub Lens Pack imports (empty lessons until authored) ─────────────────
import { SCHOOL_MACRO_MONEY_MODULE } from './macro-money';
import { SCHOOL_FIRE_MOVEMENT_MODULE } from './fire-movement';
import { SCHOOL_BEHAVIORAL_FINANCE_MODULE } from './behavioral-finance';
import { SCHOOL_BUFFETT_MUNGER_MODULE } from './buffett-munger';
import { SCHOOL_DEBT_FREE_RAMSEY_MODULE } from './debt-free-ramsey';
import { SCHOOL_RICH_LIFE_RAMIT_MODULE } from './rich-life-ramit';
import { SCHOOL_MONEY_PSYCHOLOGY_HOUSEL_MODULE } from './money-psychology-housel';
import { SCHOOL_CASHFLOW_ASSETS_MODULE } from './cashflow-assets';
import { SCHOOL_INCOME_EXPANSION_MODULE } from './income-expansion';
import { SCHOOL_MINIMALISM_MODULE } from './minimalism';
import { SCHOOL_FRUGALITY_MODULE } from './frugality';
import { SCHOOL_COAST_FI_SLOW_FI_MODULE } from './coast-fi-slow-fi';
import { SCHOOL_LIFE_DESIGN_MODULE } from './life-design';
import { SCHOOL_FI_MATH_MODULE } from './fi-math';
import { SCHOOL_RISK_MANAGEMENT_MODULE } from './risk-management';
import { SCHOOL_TAX_OPTIMIZATION_MODULE } from './tax-optimization';
import { SCHOOL_REAL_ESTATE_HOUSING_MODULE } from './real-estate-housing';
import { SCHOOL_CAREER_CAPITAL_MODULE } from './career-capital';
import { SCHOOL_HUMAN_CAPITAL_MODULE } from './human-capital';
import { SCHOOL_HARD_MONEY_MODULE } from './hard-money';
import { SCHOOL_KEYNESIAN_MMT_MODULE } from './keynesian-mmt';
import { SCHOOL_EFFICIENT_MARKET_MODULE } from './efficient-market';
import { SCHOOL_FACTOR_INVESTING_MODULE } from './factor-investing';
import { SCHOOL_DIVIDEND_INVESTING_MODULE } from './dividend-investing';
import { SCHOOL_VALUE_INVESTING_MODULE } from './value-investing';
import { SCHOOL_GROWTH_INVESTING_MODULE } from './growth-investing';
import { SCHOOL_TECHNICAL_TRADING_MODULE } from './technical-trading';
import { SCHOOL_OPTIONS_DERIVATIVES_MODULE } from './options-derivatives';
import { SCHOOL_CRYPTO_WEB3_MODULE } from './crypto-web3';
import { SCHOOL_REAL_RETURN_MODULE } from './real-return';
import { SCHOOL_FIRST_100K_MODULE } from './first-100k';
import { SCHOOL_ANTI_FRAGILITY_MODULE } from './anti-fragility';
import { SCHOOL_INEQUALITY_AWARENESS_MODULE } from './inequality-awareness';
import { SCHOOL_RELATIONSHIP_FAMILY_MONEY_MODULE } from './relationship-family-money';
import { SCHOOL_FINTECH_AUTOMATION_MODULE } from './fintech-automation';

export const SCHOOL_MODULES: LearningModule[] = [
	// ── Authored ──
	SCHOOL_BOGLEHEAD_INDEXING_MODULE,
	SCHOOL_ADULTING_LIFE_SKILLS_MODULE,
	SCHOOL_ANTI_SCAM_MODULE,
	SCHOOL_CREDIT_OPTIMIZATION_MODULE,

	// ── Stubs (empty until authored) ──
	SCHOOL_MACRO_MONEY_MODULE,
	SCHOOL_FIRE_MOVEMENT_MODULE,
	SCHOOL_BEHAVIORAL_FINANCE_MODULE,
	SCHOOL_BUFFETT_MUNGER_MODULE,
	SCHOOL_DEBT_FREE_RAMSEY_MODULE,
	SCHOOL_RICH_LIFE_RAMIT_MODULE,
	SCHOOL_MONEY_PSYCHOLOGY_HOUSEL_MODULE,
	SCHOOL_CASHFLOW_ASSETS_MODULE,
	SCHOOL_INCOME_EXPANSION_MODULE,
	SCHOOL_MINIMALISM_MODULE,
	SCHOOL_FRUGALITY_MODULE,
	SCHOOL_COAST_FI_SLOW_FI_MODULE,
	SCHOOL_LIFE_DESIGN_MODULE,
	SCHOOL_FI_MATH_MODULE,
	SCHOOL_RISK_MANAGEMENT_MODULE,
	SCHOOL_TAX_OPTIMIZATION_MODULE,
	SCHOOL_REAL_ESTATE_HOUSING_MODULE,
	SCHOOL_CAREER_CAPITAL_MODULE,
	SCHOOL_HUMAN_CAPITAL_MODULE,
	SCHOOL_HARD_MONEY_MODULE,
	SCHOOL_KEYNESIAN_MMT_MODULE,
	SCHOOL_EFFICIENT_MARKET_MODULE,
	SCHOOL_FACTOR_INVESTING_MODULE,
	SCHOOL_DIVIDEND_INVESTING_MODULE,
	SCHOOL_VALUE_INVESTING_MODULE,
	SCHOOL_GROWTH_INVESTING_MODULE,
	SCHOOL_TECHNICAL_TRADING_MODULE,
	SCHOOL_OPTIONS_DERIVATIVES_MODULE,
	SCHOOL_CRYPTO_WEB3_MODULE,
	SCHOOL_REAL_RETURN_MODULE,
	SCHOOL_FIRST_100K_MODULE,
	SCHOOL_ANTI_FRAGILITY_MODULE,
	SCHOOL_INEQUALITY_AWARENESS_MODULE,
	SCHOOL_RELATIONSHIP_FAMILY_MONEY_MODULE,
	SCHOOL_FINTECH_AUTOMATION_MODULE,
];
